import React, { useState, useRef } from "react";
import styled from "styled-components";
import Lottie from "lottie-react";
import { useTranslation } from "react-i18next";
import { ReactComponent as OcrDocumentSvg } from "@/src/assets/icons/ocr-document.svg";
import { ReactComponent as OcrUploadSvg } from "@/src/assets/icons/ocr-upload.svg";
import { ReactComponent as BetaSvg } from "@/src/assets/icons/beta.svg";
import { ReactComponent as ErrorCircleSvg } from "@/src/assets/icons/icon-error-circle.svg";
import colorSet from "@/src/styles/color";
import ocrDocumentAnimation from "@/src/assets/lottie/ocr-document.json";
import useAlert from "@/src/hooks/useAlert";
import { ocrApiSlice } from "@/src/store/apis/baseQuery";
import { Button } from "../../atom/Button";

const InstructionText = styled.span`
  color: #353c3f;
  font-size: 16px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 24px;
  word-wrap: break-word;
`;

const FileFormatInfoText = styled.span`
  color: #6e777c;
  font-size: 13px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 18px;
  word-wrap: break-word;
`;

const ButtonText = styled.span`
  color: var(--Gray-Gray-2, #222729);
  font-family: Pretendard;
  font-size: 15px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
`;

const TextContainer = styled.div`
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 4px;
  display: flex;
`;

const DocumentIcon = styled.div`
  width: 36px;
  height: 36px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const BetaBadge = styled.div`
  position: absolute;
  top: -12px;
  right: -12px;
  z-index: 1;
`;

const UploadArea = styled.div`
  display: flex;
  padding: 32px 0px 24px 0px;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  align-self: stretch;
`;

const DragDropZone = styled(UploadArea)<{ $isDragActive: boolean }>`
  border-radius: 8px;
  border: ${(props) =>
    props.$isDragActive
      ? "1px dashed var(--Primary-Blue-Blue-7, #ADCCF3)"
      : `1px dashed ${colorSet.gray8}`};
  background: ${(props) =>
    props.$isDragActive
      ? "var(--Primary-Blue-Blue-10, #F0F8FF)"
      : colorSet.gray11};
  transition: all 0.3s ease;
  min-height: 210px;
  max-height: 210px;
  box-sizing: border-box;
  justify-content: center;
`;

const OcrContainer = styled.div`
  width: 100%;
  padding-left: 16px;
  padding-right: 16px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 10px;
  display: inline-flex;
  min-height: 210px;
  max-height: 210px;
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ErrorIcon = styled.div`
  margin-right: 6px;
  display: flex;
  align-items: center;
`;

const ErrorMessage = styled.span`
  color: var(--System-Red-Red-2, #d64c4c);
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  height: 18px;
`;

export interface BookingVM {
  bookingNo?: string;
  shippingLine?: string;
  vessel?: string;
  voyageNo?: string;
  placeOfReceipt?: string;
  portOfLoading?: string;
  portOfDischarge?: string;
  placeOfDelivery?: string;
  numberOfContainer?: number;
  containerType?: string;
  etdAt?: string;
  etaAt?: string;
  docCutOffAt?: string;
  cargoCutOffAt?: string;
}

// OCR API 엔드포인트 추가
const enhancedOcrApiSlice = ocrApiSlice.injectEndpoints({
  endpoints: (build) => ({
    uploadOcrDocument: build.mutation<BookingVM, FormData>({
      query: (formData) => ({
        url: "/api/ocr",
        method: "POST",
        body: formData,
      }),
    }),
  }),
});

export const { useUploadOcrDocumentMutation } = enhancedOcrApiSlice;

interface OcrProps {
  onFileSelect?: (result: BookingVM) => void;
}

export const Ocr: React.FC<OcrProps> = ({ onFileSelect }) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const alert = useAlert();
  const [uploadOcrDocument] = useUploadOcrDocumentMutation();
  const { t } = useTranslation();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (validateFile(file)) {
        uploadFile(file);
      }
    }
  };

  const uploadFile = async (file: File) => {
    // 업로드 시작
    setIsUploading(true);

    // FormData 생성
    const formData = new FormData();
    formData.append("file", file);

    try {
      // API 요청
      const response = await uploadOcrDocument(formData).unwrap();

      // 성공 처리
      setIsUploading(false);

      if (response) {
        // 성공 알림 표시
        alert.showAlert({
          message: t("booking:add.ocr.alert.success"),
          type: "success",
        });

        // 결과 데이터 부모 컴포넌트로 전달
        if (onFileSelect) {
          onFileSelect(response);
        }

        setErrorMessage("");
      } else {
        // 데이터 없음
        alert.showAlert({
          message: t("booking:add.ocr.alert.noResult"),
          type: "error",
        });
      }
    } catch (error: any) {
      setIsUploading(false);

      // 에러 처리
      if (error.message === "Network Error" || error.status === "FETCH_ERROR") {
        alert.showAlert({
          message: t("booking:add.ocr.alert.timeout"),
          type: "error",
        });
      } else {
        alert.showAlert({
          message: t("booking:add.ocr.alert.error"),
          type: "error",
        });
      }
    }
  };

  const validateFile = (file: File): boolean => {
    const validTypes = [
      "application/pdf",
      "image/png",
      "image/jpeg",
      "image/jpg",
    ];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      setErrorMessage(t("booking:add.ocr.fileTypeOrSizeError"));
      return false;
    }

    if (file.size > maxSize) {
      setErrorMessage(t("booking:add.ocr.fileTypeOrSizeError"));
      return false;
    }

    return true;
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (e.dataTransfer.files?.[0]) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        uploadFile(file);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <OcrContainer>
      <DragDropZone
        $isDragActive={isDragActive}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
      >
        <DocumentIcon>
          {isUploading ? (
            <Lottie
              animationData={ocrDocumentAnimation}
              style={{ width: 36, height: 36 }}
            />
          ) : isDragActive ? (
            <OcrUploadSvg width="36" height="36" />
          ) : (
            <>
              <OcrDocumentSvg width="36" height="36" />
              <BetaBadge>
                <BetaSvg width="24" height="24" />
              </BetaBadge>
            </>
          )}
        </DocumentIcon>
        <TextContainer>
          <InstructionText>
            {isUploading
              ? t("booking:add.ocr.dropInProgress")
              : isDragActive
                ? t("booking:add.ocr.dropAreaPlaceholder")
                : t("booking:add.ocr.uploadTitle")}
          </InstructionText>
          {isDragActive || isUploading ? null : (
            <FileFormatInfoText>
              {t("booking:add.ocr.uploadGuide")}
            </FileFormatInfoText>
          )}
        </TextContainer>
        {isDragActive || isUploading ? null : (
          <Button
            buttonSize={40}
            buttonGrade="tertiary"
            buttonColor="black"
            onClick={handleButtonClick}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              onChange={handleFileChange}
              style={{ display: "none" }}
              id="file-upload"
            />
            <ButtonText>{t("booking:add.ocr.selectFileButton")}</ButtonText>
          </Button>
        )}
      </DragDropZone>

      <ErrorContainer
        style={{ visibility: errorMessage === "" ? "hidden" : "visible" }}
      >
        <ErrorIcon>
          <ErrorCircleSvg width="16" height="16" />
        </ErrorIcon>
        <ErrorMessage>{errorMessage}</ErrorMessage>
      </ErrorContainer>
    </OcrContainer>
  );
};
